import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, ChevronDown, Play, Award, Shield, Phone, Mail } from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const headerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setActiveDropdown(null); // Close any open dropdowns when toggling mobile menu
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const corePlatform = [
    { name: 'Enterprise Automation Platform', description: 'Complete business automation & onboarding', href: '/products#core-platform', icon: '🚀' },
    { name: 'Custom Application Development', description: 'Build any business system you need', href: '/products#core-platform', icon: '⚡' },
    { name: 'Complete Onboarding & Training', description: 'Full-service implementation support', href: '/products#core-platform', icon: '🎓' },
    { name: 'Dedicated Success Manager', description: 'Your personal automation expert', href: '/contact', icon: '👥' }
  ];

  const offTheShelfSolutions = [
    { name: 'AquaCare', description: 'Healthcare Management', href: '/products/aquacare' },
    { name: 'AquaInvoice', description: 'Financial Operations', href: '/products/aquainvoice' },
    { name: 'AquaBizz', description: 'Business Intelligence', href: '/products/aquabizz' },
    { name: 'AquaLearn', description: 'Learning Management', href: '/products/aqualearn' },
    { name: 'AquaStyle', description: 'Creative Management', href: '/products/aquastyle' }
  ];

  const coreFeatures = [
    { 
      name: 'AI & Intelligence', 
      description: 'Natural language queries, insights & autonomous operations', 
      href: '/features/ai-intelligence',
      icon: '🤖',
      capabilities: ['Conversational AI', 'Data Insights', 'Autonomous Operations', 'Smart Analytics']
    },
    { 
      name: 'Data Management', 
      description: 'Complete CRUD operations, object management & relationships', 
      href: '/features/data-management',
      icon: '📊',
      capabilities: ['Object Management', 'Record Operations', 'Data Relationships', 'Bulk Processing']
    },
    { 
      name: 'Reporting & Analytics', 
      description: 'Advanced reports, dashboards & real-time analytics', 
      href: '/features/reporting-analytics',
      icon: '📈',
      capabilities: ['Custom Reports', 'Interactive Dashboards', 'Real-time Analytics', 'Data Export']
    },
    { 
      name: 'Workflow & Automation', 
      description: 'Business process automation, triggers & scheduled jobs', 
      href: '/features/workflow-automation',
      icon: '⚡',
      capabilities: ['Process Automation', 'Scheduled Jobs', 'Custom Functions', 'Event Triggers']
    },
    { 
      name: 'Custom Development', 
      description: 'Visual page builder, custom functions & API integrations', 
      href: '/features/custom-development',
      icon: '🛠️',
      capabilities: ['Visual Page Builder', 'Custom Functions', 'API Integrations', 'Package Management']
    },
    { 
      name: 'Security & Collaboration', 
      description: 'Role-based access, sharing rules & team collaboration', 
      href: '/features/security-collaboration',
      icon: '🔐',
      capabilities: ['Role-based Access', 'Sharing Rules', 'Team Collaboration', 'Audit Trails']
    }
  ];

  const resources = [
    { name: 'Platform Demo', description: 'Watch live demonstrations', href: 'https://www.youtube.com/@nocodeAquargin', icon: Play, external: true },
    { name: 'Technical Architecture', description: 'System design & infrastructure', href: '/technical-architecture' },
    { name: 'Success Stories', description: 'Client case studies', href: '/about#success-stories' },
    { name: 'Video Tutorials', description: 'Learn on YouTube', href: 'https://www.youtube.com/@nocodeAquargin', external: true },
    { name: 'Documentation', description: 'Technical guides', href: '/documentation' },
    { name: 'Blog', description: 'Latest insights & updates', href: '/blogs' },
    { name: 'Enterprise Support', description: '24/7 dedicated support', href: '/contact' }
  ];

  const company = [
    { name: 'About Aquargin', description: 'Our mission & vision', href: '/about' },
    { name: 'Leadership Team', description: 'Meet our executives', href: '/about#leadership' },
    { name: 'Careers', description: 'Join our team', href: '/contact' },
    { name: 'News & Press', description: 'Latest updates', href: '/blogs' },
    { name: 'Contact Us', description: 'Get in touch', href: '/contact' }
  ];

  return (
    <>
      {/* Top Bar - Enterprise Contact - Hidden when scrolled */}
      <div className={`bg-gray-900 text-white py-2 text-sm transition-all duration-300 ${
        isScrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'
      }`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Award className="h-4 w-4 text-yellow-400" />
                              <span>Trusted by Enterprise Clients Worldwide</span>
            </div>
            <div className="hidden md:flex items-center space-x-2">
              <Shield className="h-4 w-4 text-green-400" />
                              <span>Enterprise Security Standards</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4" />
              <span>+91 70175 92307</span>
            </div> */}
            <div className="hidden md:flex items-center space-x-2">
              <Mail className="h-4 w-4" />
              <span>admin@aquargin.com</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header 
        ref={headerRef}
        className={`sticky top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' 
            : 'bg-white/90 backdrop-blur-sm'
        }`}
      >
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <a href="/" className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">A</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Aquargin</h1>
                  <p className="text-xs text-gray-600 -mt-1">Enterprise Automation</p>
                </div>
              </a>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {/* Solutions Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setActiveDropdown('solutions')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button 
                  className="flex items-center space-x-1 text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  <span>Solutions</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                {activeDropdown === 'solutions' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-[480px] bg-white rounded-xl shadow-xl border border-gray-200 p-6">
                      <div className="grid grid-cols-1 gap-1">
                        <div className="mb-6">
                          <div className="flex items-center mb-3">
                            <span className="text-lg mr-2">🚀</span>
                            <h3 className="text-sm font-bold text-gray-900">Our Core Platform</h3>
                            <span className="ml-2 bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs font-medium">Primary Offering</span>
                          </div>
                          {corePlatform.map((item) => (
                            <a
                              key={item.name}
                              href={item.href}
                              onClick={handleLinkClick}
                              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-primary-50 hover:border-primary-200 border border-transparent transition-all duration-200 group"
                            >
                              <div className="text-2xl flex-shrink-0 group-hover:scale-110 transition-transform">
                                {item.icon}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">{item.name}</p>
                                <p className="text-sm text-gray-600">{item.description}</p>
                              </div>
                            </a>
                          ))}
                        </div>
                        
                        <div className="border-t pt-4">
                          <div className="flex items-center mb-3">
                            <span className="text-lg mr-2">📦</span>
                            <h3 className="text-sm font-bold text-gray-900">Off-the-Shelf Solutions</h3>
                            <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium">Quick Deploy</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {offTheShelfSolutions.map((solution) => (
                              <a
                                key={solution.name}
                                href={solution.href}
                                onClick={handleLinkClick}
                                className="p-2 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200"
                              >
                                <div className="flex items-center space-x-2 mb-1">
                                  <div className="w-6 h-6 bg-gradient-to-r from-gray-400 to-gray-500 rounded flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold text-xs">{solution.name[4]}</span>
                                  </div>
                                  <p className="font-medium text-gray-900 text-sm">{solution.name}</p>
                                </div>
                                <p className="text-xs text-gray-600 ml-8">{solution.description}</p>
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Core Features Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setActiveDropdown('features')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button 
                  className="flex items-center space-x-1 text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  <span>Core Features</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                {activeDropdown === 'features' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-[520px] bg-white rounded-xl shadow-xl border border-gray-200 p-6">
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Platform Capabilities</h3>
                        <p className="text-sm text-gray-600">Comprehensive enterprise automation features</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {coreFeatures.map((feature) => (
                          <a
                            key={feature.name}
                            href={feature.href}
                            onClick={handleLinkClick}
                            className="p-4 rounded-lg hover:bg-primary-50 hover:border-primary-200 border border-transparent transition-all duration-200 group"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="text-2xl flex-shrink-0 group-hover:scale-110 transition-transform">
                                {feature.icon}
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-1">
                                  {feature.name}
                                </p>
                                <p className="text-xs text-gray-600 mb-2 leading-relaxed">
                                  {feature.description}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {feature.capabilities.slice(0, 2).map((capability, idx) => (
                                    <span 
                                      key={idx}
                                      className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                                    >
                                      {capability}
                                    </span>
                                  ))}
                                  {feature.capabilities.length > 2 && (
                                    <span className="text-xs text-gray-500">
                                      +{feature.capabilities.length - 2} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <a 
                          href="/features" 
                          className="flex items-center justify-center w-full py-2 px-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300"
                        >
                          Explore All Features →
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing */}
              <a href="/pricing" onClick={handleLinkClick} className="text-gray-700 hover:text-primary font-medium transition-colors">
                Pricing
              </a>

              {/* Resources Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setActiveDropdown('resources')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button 
                  className="flex items-center space-x-1 text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  <span>Resources</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                {activeDropdown === 'resources' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-6">
                      <div className="space-y-1">
                        {resources.map((resource) => (
                          <a
                            key={resource.name}
                            href={resource.href}
                            onClick={handleLinkClick}
                            target={resource.external ? '_blank' : undefined}
                            rel={resource.external ? 'noopener noreferrer' : undefined}
                            className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            {resource.icon && <resource.icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />}
                            <div>
                              <p className="font-medium text-gray-900">{resource.name}</p>
                              <p className="text-sm text-gray-600">{resource.description}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setActiveDropdown('company')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button 
                  className="flex items-center space-x-1 text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  <span>Company</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                {activeDropdown === 'company' && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-6">
                      <div className="space-y-1">
                        {company.map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            onClick={handleLinkClick}
                            className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <p className="text-sm text-gray-600">{item.description}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <a href="https://app.aquargin.com/login" className="text-gray-700 hover:text-primary font-medium transition-colors">
                Sign In
              </a>
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                Start Free Trial
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-700 hover:text-primary transition-colors"
              onClick={toggleMenu}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden mt-4 pb-4 border-t border-gray-200"
            >
              <div className="pt-4 space-y-4">
                {/* Mobile Solutions */}
                <div>
                  <button
                    onClick={() => toggleDropdown('mobile-solutions')}
                    className="flex items-center justify-between w-full py-2 text-gray-700 font-medium"
                  >
                    <span>Solutions</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'mobile-solutions' ? 'rotate-180' : ''}`} />
                  </button>
                  {activeDropdown === 'mobile-solutions' && (
                    <div className="pl-4 mt-2 space-y-3">
                      <div>
                        <div className="flex items-center mb-2">
                          <span className="text-sm mr-1">🚀</span>
                          <p className="text-sm font-semibold text-gray-900">Core Platform</p>
                        </div>
                        {corePlatform.map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            onClick={handleLinkClick}
                            className="block py-1.5 pl-4 text-sm text-gray-600 hover:text-primary transition-colors"
                          >
                            {item.icon} {item.name}
                          </a>
                        ))}
                      </div>
                      <div className="border-t pt-2">
                        <div className="flex items-center mb-2">
                          <span className="text-sm mr-1">📦</span>
                          <p className="text-sm font-semibold text-gray-900">Off-the-Shelf</p>
                        </div>
                        {offTheShelfSolutions.map((solution) => (
                          <a
                            key={solution.name}
                            href={solution.href}
                            onClick={handleLinkClick}
                            className="block py-1.5 pl-4 text-sm text-gray-600 hover:text-primary transition-colors"
                          >
                            {solution.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile Core Features */}
                <div>
                  <button
                    onClick={() => toggleDropdown('mobile-features')}
                    className="flex items-center justify-between w-full py-2 text-gray-700 font-medium"
                  >
                    <span>Core Features</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'mobile-features' ? 'rotate-180' : ''}`} />
                  </button>
                  {activeDropdown === 'mobile-features' && (
                    <div className="pl-4 mt-2 space-y-3">
                      {coreFeatures.map((feature) => (
                        <a
                          key={feature.name}
                          href={feature.href}
                          onClick={handleLinkClick}
                          className="block py-2"
                        >
                          <div className="flex items-start space-x-3">
                            <span className="text-lg flex-shrink-0">{feature.icon}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{feature.name}</p>
                              <p className="text-xs text-gray-600 mt-0.5">{feature.description}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {feature.capabilities.slice(0, 2).map((capability, idx) => (
                                  <span 
                                    key={idx}
                                    className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                                  >
                                    {capability}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <a href="/pricing" onClick={handleLinkClick} className="block py-2 text-gray-700 font-medium">
                  Pricing
                </a>

                {/* Mobile Resources */}
                <div>
                  <button
                    onClick={() => toggleDropdown('mobile-resources')}
                    className="flex items-center justify-between w-full py-2 text-gray-700 font-medium"
                  >
                    <span>Resources</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'mobile-resources' ? 'rotate-180' : ''}`} />
                  </button>
                  {activeDropdown === 'mobile-resources' && (
                    <div className="pl-4 mt-2 space-y-2">
                      {resources.map((resource) => (
                        <a
                          key={resource.name}
                          href={resource.href}
                          onClick={handleLinkClick}
                          target={resource.external ? '_blank' : undefined}
                          rel={resource.external ? 'noopener noreferrer' : undefined}
                          className="block py-2 text-gray-600 hover:text-primary transition-colors"
                        >
                          {resource.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile Company */}
                <div>
                  <button
                    onClick={() => toggleDropdown('mobile-company')}
                    className="flex items-center justify-between w-full py-2 text-gray-700 font-medium"
                  >
                    <span>Company</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'mobile-company' ? 'rotate-180' : ''}`} />
                  </button>
                  {activeDropdown === 'mobile-company' && (
                    <div className="pl-4 mt-2 space-y-2">
                      {company.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          onClick={handleLinkClick}
                          className="block py-2 text-gray-600 hover:text-primary transition-colors"
                        >
                          {item.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile CTA */}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <a href="https://app.aquargin.com/login" className="block w-full text-left py-2 text-gray-700 font-medium">
                    Sign In
                  </a>
                  <a href="https://app.aquargin.com/register" className="block w-full bg-gradient-to-r from-primary-500 to-secondary-500 text-white py-3 rounded-lg font-semibold text-center">
                    Start Free Trial
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </nav>
      </header>
    </>
  );
};

export default Header; 